import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Backdrop,
	Box,
	Chip,
	CircularProgress,
	InputAdornment,
	Link,
	MenuItem,
	Pagination,
	Paper,
	Select,
	SelectChangeEvent,
	Snackbar,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';

import { pageSizeByDefault, pageSizeOptions } from 'constants/constants';
import { PageNoResult } from 'features/common/components/PageNoResults';
import { defaultAlertState } from 'features/common/constants';
import { convertFiltersToRecord } from 'features/common/helpers';
import { useDebounceState } from 'features/common/hooks/useDebounceState';
import { FilterModel, IHeadCell, ISort, ShowAlertState } from 'features/common/types';
import { extractErrorMessage } from 'features/quotation/helpers';

import { getPassengersList } from '../../services';
import { IPassengerList } from '../../types';
import { PERSON_TYPE_TAB_URL_MAP, PassengersListVariant, PersonTypeTab, personTypeFromUrl } from './constants';
import { buildPassengerDetailUrl } from '../../helpers/detailView';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { getAgencyIdOrDefault } from 'features/auth/constants';
import { PassengersFilters } from './PassengersFilters';
import { PassengersAdvancedSearch } from './PassengersAdvancedSearch';
import { useStyles } from './styles';
import { IPassengerAutocomplete } from 'features/salesOrder/types';

interface PassengersDataListProps {
	variant: PassengersListVariant;
	fixedFilters: Record<string, boolean>;
	showPersonTypeTabs?: boolean;
}

const passengerHeaderCells: IHeadCell[] = [
	{ field: 'id', sortable: true, headerName: 'ID' },
	{ field: 'name', sortable: true, headerName: 'Nombre' },
	{ field: 'lastName', sortable: true, headerName: 'Apellido' },
	{ field: 'countryName', sortable: true, headerName: 'País' },
	{ field: 'documentType', sortable: false, headerName: 'Tipo doc.' },
	{ field: 'documentNumber', sortable: false, headerName: 'Documento de facturación' },
];

const clientHeaderCells: IHeadCell[] = [
	{ field: 'id', sortable: true, headerName: 'ID' },
	{ field: 'name', sortable: true, headerName: 'Nombre / Razón social' },
	{ field: 'lastName', sortable: true, headerName: 'Apellido / Nom. fantasía' },
	{ field: 'countryName', sortable: true, headerName: 'País' },
	{ field: 'documentType', sortable: false, headerName: 'Tipo doc.' },
	{ field: 'documentNumber', sortable: false, headerName: 'Documento de facturación' },
	{ field: 'isPhysicalPerson', sortable: true, headerName: 'Tipo' },
];

const PERSON_TYPE_TABS: { value: PersonTypeTab; label: string }[] = [
	{ value: 'all', label: 'Todos' },
	{ value: 'physical', label: 'Personas físicas' },
	{ value: 'legal', label: 'Personas jurídicas' },
];

const DEFAULT_SORT: ISort[] = [{ field: 'name', order: 'asc' }];

const formatOptionalValue = (value?: string | number | null): string => {
	if (value === undefined || value === null || value === '') return '—';
	return String(value);
};

const getTabFilters = (personTypeTab: PersonTypeTab): Record<string, boolean> => {
	if (personTypeTab === 'physical') return { isPhysicalPerson: true };
	if (personTypeTab === 'legal') return { isPhysicalPerson: false };
	return {};
};

export const PassengersDataList = ({
	variant,
	fixedFilters,
	showPersonTypeTabs = false,
}: PassengersDataListProps): JSX.Element => {
	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();

	const [loading, setLoading] = useState<boolean>(false);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [currentSize, setCurrentSize] = useState<number>(pageSizeByDefault);
	const [pageTotal, setPageTotal] = useState<number>(0);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchValue, setSearchValue] = useState<string>('');
	const [sortFields, setSortFields] = useState<ISort[]>(DEFAULT_SORT);
	const [data, setData] = useState<IPassengerList[]>([]);
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>([]);
	const [advancedSearchPassenger, setAdvancedSearchPassenger] = useState<IPassengerAutocomplete | null>(null);

	const personTypeTab = personTypeFromUrl(location.search);
	const prevPersonTypeTab = useRef(personTypeTab);
	const debouncedSearch = useDebounceState(searchValue, 300);
	const headerCells = variant === 'passenger' ? passengerHeaderCells : clientHeaderCells;

	const tabFilters = useMemo(
		() => (showPersonTypeTabs ? getTabFilters(personTypeTab) : {}),
		[showPersonTypeTabs, personTypeTab],
	);

	const mergedFilters = useMemo(
		() => ({
			...fixedFilters,
			...tabFilters,
			...convertFiltersToRecord(filterApplied),
			...(advancedSearchPassenger ? { id: advancedSearchPassenger.passengerId } : {}),
		}),
		[fixedFilters, tabFilters, filterApplied, advancedSearchPassenger],
	);

	const filtersKey = JSON.stringify(mergedFilters);

	useEffect(() => {
		const abortController = new AbortController();

		const loadData = async () => {
			try {
				setLoading(true);
				const { data: response } = await getPassengersList(
					currentPage,
					currentSize,
					sortFields,
					mergedFilters,
					debouncedSearch || undefined,
					abortController.signal,
				);

				if (abortController.signal.aborted) return;

				const total = response.total ?? 0;
				setTotalRecords(total);
				setPageTotal(total === 0 ? 0 : Math.ceil(total / currentSize));
				setData(response.elements ?? []);
			} catch (error) {
				if (abortController.signal.aborted || axios.isCancel(error)) return;
				setAlert({
					show: true,
					severity: 'error',
					message: extractErrorMessage(error, 'Ocurrió un error al recibir el listado'),
				});
			} finally {
				if (!abortController.signal.aborted) {
					setLoading(false);
				}
			}
		};

		loadData();

		return () => {
			abortController.abort();
		};
	}, [currentPage, currentSize, sortFields, debouncedSearch, filtersKey, personTypeTab]);

	useEffect(() => {
		if (prevPersonTypeTab.current !== personTypeTab) {
			prevPersonTypeTab.current = personTypeTab;
			setCurrentPage(1);
			setAdvancedSearchPassenger(null);
		}
	}, [personTypeTab]);

	const handleAdvancedSearchSelect = useCallback((passenger: IPassengerAutocomplete) => {
		setAdvancedSearchPassenger(passenger);
		setCurrentPage(1);
	}, []);

	const handleAdvancedSearchClear = useCallback(() => {
		setAdvancedSearchPassenger(null);
		setCurrentPage(1);
	}, []);

	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
		setCurrentPage(1);
	};

	const handleFiltersChange = useCallback((filters: FilterModel[]) => {
		setFilterApplied(filters);
		setCurrentPage(1);
	}, []);

	const handleColumnSort = (field: string) => {
		setSortFields((prev) => {
			const current = prev.find((item) => item.field === field);
			if (!current) return [{ field, order: 'asc' }];
			if (current.order === 'asc') return [{ field, order: 'desc' }];
			return DEFAULT_SORT;
		});
		setCurrentPage(1);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};

	const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	const handlePersonTypeTabChange = (_event: React.SyntheticEvent, value: PersonTypeTab) => {
		setCurrentPage(1);

		const params = new URLSearchParams(location.search);
		const urlValue = PERSON_TYPE_TAB_URL_MAP[value];
		if (urlValue) {
			params.set('tipo', urlValue);
		} else {
			params.delete('tipo');
		}
		const search = params.toString();
		history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
	};

	const detailViewMode = variant === 'client' ? 'client' : 'passenger';
	const auth = useSelector((state: RootState) => state.auth);
	// Los links al detalle llevan la BU actual para que abran en el ambiente correcto
	const currentAgencyId = getAgencyIdOrDefault(auth.country);

	const handleOpenDetail = (id: string) => {
		window.open(buildPassengerDetailUrl(id, detailViewMode, currentAgencyId), '_blank', 'noopener,noreferrer');
	};

	const handleCloseAlert = () => setAlert(defaultAlertState);

	const searchPlaceholder =
		variant === 'passenger' ? 'Buscar por nombre, apellido, ID o país' : 'Buscar por nombre, razón social, ID o país';

	const getSortDirection = (field: string): 'asc' | 'desc' | false => {
		return sortFields.find((item) => item.field === field)?.order ?? false;
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>

			{showPersonTypeTabs && (
				<Box sx={{ mb: 2 }}>
					<Tabs
						value={personTypeTab}
						onChange={handlePersonTypeTabChange}
						variant="scrollable"
						allowScrollButtonsMobile
					>
						{PERSON_TYPE_TABS.map((tab) => (
							<Tab key={tab.value} label={tab.label} value={tab.value} />
						))}
					</Tabs>
				</Box>
			)}

			<Paper className={classes.filtersCard} elevation={0}>
				<Box className={classes.toolbarRow}>
					<TextField
						label="Buscar"
						placeholder={searchPlaceholder}
						variant="outlined"
						size="small"
						value={searchValue}
						onChange={handleSearchChange}
						sx={{ flex: 1, minWidth: 280 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon color="action" />
								</InputAdornment>
							),
						}}
					/>
					<Typography className={classes.resultsCount}>
						{totalRecords.toLocaleString('es-UY')} {totalRecords === 1 ? 'resultado' : 'resultados'}
					</Typography>
				</Box>
				<PassengersAdvancedSearch
					variant={variant}
					personTypeTab={personTypeTab}
					selectedPassenger={advancedSearchPassenger}
					onSelect={handleAdvancedSearchSelect}
					onClear={handleAdvancedSearchClear}
				/>
			</Paper>

			<PassengersFilters onFiltersChange={handleFiltersChange} />

			<Paper className={classes.tablePaper} elevation={0}>
				<TableContainer>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								{headerCells.map((cell) =>
									cell.sortable ? (
										<TableCell
											key={cell.field}
											className={classes.tableHeader}
											sortDirection={getSortDirection(cell.field)}
										>
											<TableSortLabel
												active={sortFields.some((item) => item.field === cell.field)}
												sx={{
													color: 'white !important',
													'& .MuiTableSortLabel-icon': { color: 'white !important' },
													'&:hover': { color: 'rgba(255,255,255,0.85) !important' },
												}}
												direction={getSortDirection(cell.field) || 'asc'}
												onClick={() => handleColumnSort(cell.field)}
											>
												{cell.headerName}
											</TableSortLabel>
										</TableCell>
									) : (
										<TableCell key={cell.field} className={classes.tableHeader}>
											{cell.headerName}
										</TableCell>
									),
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{!data.length ? (
								<TableRow>
									<TableCell colSpan={headerCells.length}>
										<PageNoResult />
									</TableCell>
								</TableRow>
							) : (
								data.map((item) => (
									<TableRow key={item.id} className={classes.tableRow} onClick={() => handleOpenDetail(item.id)}>
										<TableCell>
											<Link
												href={buildPassengerDetailUrl(item.id, detailViewMode, currentAgencyId)}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
											>
												{item.id}
											</Link>
										</TableCell>
										<TableCell>{item.name}</TableCell>
										<TableCell>{item.lastName}</TableCell>
										<TableCell>{item.countryName}</TableCell>
										<TableCell>{formatOptionalValue(item.documentType)}</TableCell>
										<TableCell>{formatOptionalValue(item.documentNumber)}</TableCell>
										{variant === 'client' && (
											<TableCell>
												<Chip
													label={item.isPhysicalPerson ? 'Persona física' : 'Persona jurídica'}
													size="small"
													color={item.isPhysicalPerson ? 'success' : 'info'}
													variant="outlined"
												/>
											</TableCell>
										)}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			{totalRecords > 0 && (
				<Box className={classes.paginationBar}>
					<Select value={currentSize} onChange={handleRowsPerPageChange} size="small">
						{pageSizeOptions.map((value) => (
							<MenuItem key={value} value={value}>
								{value} por página
							</MenuItem>
						))}
					</Select>
					<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
				</Box>
			)}

			<Snackbar open={alert.show} autoHideDuration={3000} onClose={handleCloseAlert}>
				<Alert variant="filled" severity={alert.severity} onClose={handleCloseAlert}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
